import React from 'react';
import {useTranslation} from 'react-i18next';
import type {
	InputMarketProps,
	MarketNamingTemplateKeys,
} from '../MarketNamingTemplateField';

export const matchesMarketNamingTemplate = (
	markets: InputMarketProps[],
	value: string,
	templatesToUse: MarketNamingTemplateKeys,
): boolean =>
	markets.some(market =>
		market[templatesToUse]?.some(template =>
			new RegExp(template.template).test(value),
		),
	);

export const isFreeTextValue = (
	markets: InputMarketProps[],
	value: string | null | undefined,
	templatesToUse: MarketNamingTemplateKeys,
): boolean => {
	if (markets.length === 0 || !value) {
		return false;
	}

	return !matchesMarketNamingTemplate(markets, value, templatesToUse);
};

export const useInferredFreeTextMode = (
	markets: InputMarketProps[],
	value: string | null | undefined,
	templatesToUse: MarketNamingTemplateKeys,
	savedValueKey?: string | null,
) => {
	const [freeText, setFreeTextState] = React.useState(() =>
		isFreeTextValue(markets, value, templatesToUse),
	);
	const userSelected = React.useRef(false);
	const previousSource = React.useRef({savedValueKey, value});

	React.useEffect(() => {
		const sourceChanged =
			previousSource.current.savedValueKey !== savedValueKey ||
			previousSource.current.value !== value;

		if (sourceChanged) {
			previousSource.current = {savedValueKey, value};
			userSelected.current = false;
		}

		if (!userSelected.current) {
			setFreeTextState(isFreeTextValue(markets, value, templatesToUse));
		}
	}, [markets, savedValueKey, templatesToUse, value]);

	const setFreeText = React.useCallback((selected: boolean) => {
		userSelected.current = true;
		setFreeTextState(selected);
	}, []);

	return [freeText, setFreeText] as const;
};

export const useMarketNamingTemplateValidation = (
	markets: InputMarketProps[],
	namingHint: string,
	templatesToUse: MarketNamingTemplateKeys,
): {
	validateFieldValue: (value: string) => boolean | string;
	isMarketSelected: boolean;
	placeholder: string;
} => {
	const {t} = useTranslation('components/marketnamingtemplatevalidation');

	const isMarketSelected = markets.length > 0;
	const placeholder = isMarketSelected
		? namingHint
		: t('FieldDisabledPlaceholder');

	const validateFieldValue = React.useCallback(
		(value: string) =>
			!isMarketSelected ||
			matchesMarketNamingTemplate(markets, value, templatesToUse) ||
			namingHint,
		[isMarketSelected, markets, namingHint, templatesToUse],
	);

	return {
		validateFieldValue,
		isMarketSelected,
		placeholder,
	};
};
